import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import apiService from '../services/api.service';
import { RootState } from '../store';
import { showSnackbar } from '../store/slices/uiSlice';

export interface SubcontractProjectOption {
  code: string;
  name: string;
  Code: string;
  Name: string;
  [key: string]: any;
}

const readCompanyId = (): number | undefined => {
  const value = Number(localStorage.getItem('selectedCompanyId') || '');
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

export const useSelectedCompanyId = (): number | undefined => {
  const [companyId, setCompanyId] = useState<number | undefined>(() => readCompanyId());

  useEffect(() => {
    const syncCompany = (event?: Event) => {
      const eventCompanyId = Number((event as CustomEvent | undefined)?.detail?.companyId || 0);
      setCompanyId(eventCompanyId > 0 ? eventCompanyId : readCompanyId());
    };
    window.addEventListener('company-changed', syncCompany as EventListener);
    window.addEventListener('storage', syncCompany);
    return () => {
      window.removeEventListener('company-changed', syncCompany as EventListener);
      window.removeEventListener('storage', syncCompany);
    };
  }, []);

  return companyId;
};

export const useSubcontractProjects = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const companyId = useSelectedCompanyId();
  const [projects, setProjects] = useState<SubcontractProjectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!companyId || !user) {
      setProjects([]);
      setLoading(false);
      return () => { cancelled = true; };
    }

    const load = async () => {
      setLoading(true);
      try {
        const response: any = await apiService.listProjects(companyId);
        const rows = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
        const normalized = rows
          .map((project: any) => {
            const code = String(project?.code || project?.Code || project?.ProjectCode || '').trim();
            const name = String(project?.name || project?.Name || project?.ProjectName || '').trim();
            return { ...project, code, name, Code: code, Name: name };
          })
          .filter((project: SubcontractProjectOption) => project.code);
        if (!cancelled) setProjects(normalized);
      } catch (error) {
        if (!cancelled) {
          setProjects([]);
          dispatch(showSnackbar({ message: 'Failed to load projects for the selected company', severity: 'error' }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [companyId, dispatch, user]);

  return { companyId, projects, loading };
};
